import React, { useEffect, useState, useMemo } from 'react';
import { Table, Tag, Dropdown, Button, Badge, Input, Select, Empty, Row, Col, Modal, Space, Tooltip } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FilterOutlined, ReloadOutlined, DownOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, FileExcelOutlined } from '@ant-design/icons';
import { Popconfirm, message } from 'antd';
import PersianDatePicker from '../../PersianDatePicker';
import moment from 'moment-jalaali';
import { leavesApi } from '../../../utils/api';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import { getLast30DaysRange } from '../../../utils/dateUtils';
import { formatLeaveDurationLabel } from '../../../utils/leaveDurationUtils';
import { BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../../constants/ui';

const { Option } = Select;

const DEFAULT_STATUS_OPTIONS = [
  { value: 'pending', label: 'در انتظار' },
  { value: 'approved', label: 'تایید شده' },
  { value: 'rejected', label: 'رد شده' },
];

const LeavesListView = ({
  onFilterChange,
  data = [],
  loading = false,
  pagination = {},
  onDelete,
  onEdit,
  onRefresh,
  navigateToDetail,
  filterSnapshot,
  filterOptions = {},
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  // پیش‌فرض: 30 روز گذشته
  const defaultRange = useMemo(() => getLast30DaysRange(), []);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedLeaveType, setSelectedLeaveType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const isFirstRender = React.useRef(true);

  const employees = Array.isArray(filterOptions.employees) ? filterOptions.employees : [];
  const leaveTypes = Array.isArray(filterOptions.leave_types) ? filterOptions.leave_types : [];
  const statusOptions = useMemo(() => {
    const fromApi = Array.isArray(filterOptions.statuses) ? filterOptions.statuses : [];
    if (!fromApi.length) return DEFAULT_STATUS_OPTIONS;
    return fromApi.map((s) => ({
      value: String(s.value ?? s.id ?? ''),
      label: s.label || s.name || String(s.value ?? s.id ?? ''),
    })).filter((s) => s.value);
  }, [filterOptions.statuses]);

  useEffect(() => {
    if (!filterSnapshot) return;
    if (filterSnapshot.startDate != null) setStartDate(filterSnapshot.startDate);
    if (filterSnapshot.endDate != null) setEndDate(filterSnapshot.endDate);
    if (filterSnapshot.name != null) setSearchText(filterSnapshot.name);
    if (filterSnapshot.employeeId != null) {
      setSelectedEmployee(String(filterSnapshot.employeeId));
    }
    if (filterSnapshot.leaveTypeId != null) {
      setSelectedLeaveType(String(filterSnapshot.leaveTypeId));
    }
    if (filterSnapshot.status != null) {
      setSelectedStatus(filterSnapshot.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSnapshot]);

  // تابع تبدیل اعداد فارسی به انگلیسی
  const persianToEnglishDigits = (str) => {
    if (!str) return str;
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    let result = String(str);
    persianDigits.forEach((persian, index) => {
      result = result.replace(new RegExp(persian, 'g'), englishDigits[index]);
    });
    return result;
  };

  // تابع برای فرمت تاریخ جلالی با اعداد انگلیسی
  const formatJalaliDate = (date) => {
    if (!date) return null;
    
    // اگر رشته است، اعداد فارسی را به انگلیسی تبدیل کن
    if (typeof date === 'string') {
      return persianToEnglishDigits(date);
    }
    
    // اگر DateObject از react-multi-date-picker است
    if (date.format && typeof date.format === 'function') {
      // استفاده از format برای گرفتن تاریخ
      const formatted = date.format('YYYY/MM/DD');
      // تبدیل اعداد فارسی به انگلیسی
      return persianToEnglishDigits(formatted);
    }
    
    // اگر DateObject با jYear, jMonth, jDay است
    if (date.jYear !== undefined && date.jMonth !== undefined && date.jDay !== undefined) {
      const jYear = date.jYear;
      const jMonth = String(date.jMonth + 1).padStart(2, '0');
      const jDay = String(date.jDay).padStart(2, '0');
      return `${jYear}/${jMonth}/${jDay}`;
    }
    
    // اگر moment object است
    if (date.jYear && typeof date.jYear === 'function') {
      const jYear = date.jYear();
      const jMonth = String(date.jMonth() + 1).padStart(2, '0');
      const jDay = String(date.jDate()).padStart(2, '0');
      return `${jYear}/${jMonth}/${jDay}`;
    }
    
    return null;
  };

  // تبدیل داده‌های API به فرمت مورد نیاز جدول
  const mappedData = useMemo(() => {
    return (data || []).map((item) => {
      let durationLabel = formatLeaveDurationLabel(item.duration, item.half_day_type);
      if (String(item.duration).toLowerCase() === 'hourly' && (item.start_time || item.end_time)) {
        const from = item.start_time ? String(item.start_time).slice(0, 5) : '—';
        const to = item.end_time ? String(item.end_time).slice(0, 5) : '—';
        durationLabel = `ساعتی (${from}–${to})`;
      }
      const leaveDateRaw = item.leave_date || item.date;
      return {
        id: item.id,
        unique_id: item.unique_id,
        employee: item.employee?.name || item.user?.name || '--',
        employeeId: item.employee?.id || item.user_id,
        leaveDate: leaveDateRaw ? moment(leaveDateRaw).format('jYYYY/jMM/jDD') : '--',
        day: item.day || '--',
        duration: durationLabel,
        status: item.status || 'pending',
        paid: item.paid || false,
        leaveType: item.type?.name || item.type?.type_name || '--',
        leaveTypeId: item.type?.id || item.leave_type_id,
        leaveTypeColor: item.type?.color,
        over_utilized: item.over_utilized || false,
        workflow: item.workflow || null,
      };
    });
  }, [data]);

  const buildCurrentListFilters = () => {
    const filters = {};
    if (searchText) filters.name = searchText;
    if (startDate) {
      const dateStr = startDate.format ? formatJalaliDate(startDate) : startDate;
      if (dateStr) filters.startDate = dateStr;
    }
    if (endDate) {
      const dateStr = endDate.format ? formatJalaliDate(endDate) : endDate;
      if (dateStr) filters.endDate = dateStr;
    }
    if (selectedEmployee !== 'all') filters.employeeId = selectedEmployee;
    if (selectedLeaveType !== 'all') filters.leaveTypeId = selectedLeaveType;
    if (selectedStatus !== 'all') filters.status = selectedStatus;
    return filters;
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await leavesApi.exportList(buildCurrentListFilters());
      message.success('خروجی اکسل در حال دانلود است');
    } catch (error) {
      console.error('Export leaves:', error);
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExporting(false);
    }
  };

  // اعمال فیلترها با debounce
  React.useEffect(() => {
    // در اولین render اجرا نشود (چون Leaves.js خودش initial load را انجام می‌دهد)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!onFilterChange) return;

    const timer = setTimeout(() => {
      onFilterChange(buildCurrentListFilters());
    }, 800); // debounce با تاخیر 800ms
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, startDate, endDate, selectedEmployee, selectedLeaveType, selectedStatus]);

  const handleView = (record) => {
    if (navigateToDetail) {
      navigateToDetail(`/hr/leaves/${record.id}`);
    }
  };

  const handleEdit = (record) => {
    if (onEdit) {
      onEdit(record);
      return;
    }
    if (navigateToDetail) {
      navigateToDetail(`/hr/leaves/${record.id}/edit`);
    }
  };

  const handleDelete = async (record) => {
    if (onDelete) {
      await onDelete(record.id);
    }
  };

  // عملیات گروهی
  const handleBulkAction = async (actionType, status = null) => {
    if (selectedRowKeys.length === 0) {
      message.warning('لطفاً حداقل یک مرخصی را انتخاب کنید');
      return;
    }

    if (actionType === 'delete') {
      Modal.confirm({
        title: 'حذف مرخصی‌ها',
        content: `آیا از حذف ${selectedRowKeys.length} مرخصی انتخاب شده اطمینان دارید؟`,
        okText: 'بله، حذف کن',
        cancelText: 'لغو',
        okButtonProps: { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } },
        cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
        onOk: async () => {
          setBulkActionLoading(true);
          try {
            await leavesApi.applyQuickAction(actionType, selectedRowKeys);
            message.success('مرخصی‌ها با موفقیت حذف شدند');
            setSelectedRowKeys([]);
            const currentFilters = buildCurrentListFilters();
            if (onRefresh) {
              onRefresh(currentFilters);
            } else if (onFilterChange) {
              onFilterChange(currentFilters);
            }
          } catch (error) {
            console.error('Error applying bulk action:', error);
            message.error('خطا در حذف مرخصی‌ها');
          } finally {
            setBulkActionLoading(false);
          }
        },
      });
      return;
    }

    // برای change-leave-status باید status مشخص باشد
    if (actionType === 'change-leave-status' && status) {
      setBulkActionLoading(true);
      try {
        await leavesApi.applyQuickAction(actionType, selectedRowKeys, status);
        const statusTexts = {
          approved: 'تایید شده',
          pending: 'در انتظار',
          rejected: 'رد شده',
        };
        message.success(`وضعیت ${selectedRowKeys.length} مرخصی به "${statusTexts[status] || status}" تغییر کرد`);
        setSelectedRowKeys([]);
        const currentFilters = buildCurrentListFilters();
        if (onRefresh) {
          onRefresh(currentFilters);
        } else if (onFilterChange) {
          onFilterChange(currentFilters);
        }
      } catch (error) {
        console.error('Error applying bulk action:', error);
        message.error('خطا در تغییر وضعیت مرخصی‌ها');
      } finally {
        setBulkActionLoading(false);
      }
    }
  };

  // منوی عملیات گروهی
  const bulkActionMenuItems = [
    ...(onEdit
      ? [
          {
            key: 'change-status-approved',
            label: 'تایید مرخصی‌ها',
            icon: <CheckCircleOutlined className="text-green-600" />,
            onClick: () => handleBulkAction('change-leave-status', 'approved'),
          },
          {
            key: 'change-status-pending',
            label: 'در انتظار قرار دادن',
            icon: <ClockCircleOutlined className="text-orange-600" />,
            onClick: () => handleBulkAction('change-leave-status', 'pending'),
          },
          {
            key: 'change-status-rejected',
            label: 'رد کردن مرخصی‌ها',
            icon: <CloseCircleOutlined className="text-red-600" />,
            onClick: () => handleBulkAction('change-leave-status', 'rejected'),
          },
        ]
      : []),
    ...(onEdit && onDelete ? [{ type: 'divider' }] : []),
    ...(onDelete
      ? [
          {
            key: 'delete',
            label: 'حذف مرخصی‌ها',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => handleBulkAction('delete'),
          },
        ]
      : []),
  ];


  const columns = [
    {
      title: 'کارمند',
      dataIndex: 'employee',
      key: 'employee',
      width: 200,
      sorter: true,
      render: (text) => (
        <span style={{ fontFamily: 'CustomFont' }}>{text || '--'}</span>
      ),
    },
    {
      title: 'تاریخ مرخصی',
      dataIndex: 'leaveDate',
      key: 'leaveDate',
      width: 150,
      sorter: true,
      render: (text) => (
        <span style={{ fontFamily: 'CustomFont' }}>{text || '--'}</span>
      ),
    },
    {
      title: 'مدت زمان',
      dataIndex: 'duration',
      key: 'duration',
      width: 150,
      sorter: true,
      render: (text) => (
        <span style={{ fontFamily: 'CustomFont' }}>{text || '--'}</span>
      ),
    },
    {
      title: 'وضعیت مرخصی',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      sorter: true,
      render: (status, record) => {
        const statusConfig = {
          pending: { color: 'orange', text: 'در انتظار' },
          approved: { color: 'green', text: 'تایید شده' },
          rejected: { color: 'red', text: 'رد شده' },
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        const wf = record.workflow;
        return (
          <div style={{ fontFamily: 'CustomFont' }}>
            <Tag color={config.color} className="m-0">
              {config.text}
            </Tag>
            {wf?.status_label ? (
              <div className="text-[10px] text-slate-500 mt-1 leading-tight">
                {wf.status_label}
                {wf.current_step_order != null && wf.total_steps != null
                  ? ` · ${wf.current_step_order}/${wf.total_steps}`
                  : ''}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'نوع مرخصی',
      dataIndex: 'leaveType',
      key: 'leaveType',
      width: 150,
      sorter: true,
      render: (text) => (
        <span style={{ fontFamily: 'CustomFont' }}>{text || '--'}</span>
      ),
    },
    {
      title: 'پرداخت شده',
      dataIndex: 'paid',
      key: 'paid',
      width: 120,
      sorter: true,
      render: (paid) => (
        <Badge
          status={paid ? 'success' : 'default'}
          text={
            <span style={{ fontFamily: 'CustomFont' }}>
              {paid ? 'بله' : 'خیر'}
            </span>
          }
        />
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 160,
      fixed: 'left',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="مشاهده">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            />
          </Tooltip>
          {onEdit ? (
            <Tooltip title="ویرایش">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              />
            </Tooltip>
          ) : null}
          {onDelete && (
            <Popconfirm
              title="حذف مرخصی"
              description="آیا از حذف این مرخصی اطمینان دارید؟"
              onConfirm={() => handleDelete(record)}
              okText="بله، حذف کن"
              cancelText="لغو"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="حذف">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  danger
                  className="hover:bg-red-50 dark:hover:bg-red-900/20"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const handleResetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedEmployee('all');
    setSelectedLeaveType('all');
    setSelectedStatus('all');
  };

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      {/* فیلترهای پیشرفته */}
      <div className="mb-6">
        <button
          onClick={() => setFilterExpanded(!filterExpanded)}
          className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <FilterOutlined className="text-gray-600 dark:text-gray-400" />
            <span
              className="text-sm font-semibold text-gray-900 dark:text-gray-100"
              style={{ fontFamily: 'CustomFont' }}
            >
              فیلترهای بیشتر
            </span>
          </div>
          {filterExpanded ? (
            <span className="text-gray-400 dark:text-gray-500">▲</span>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">▼</span>
          )}
        </button>
        
        {filterExpanded && (
          <div className="mt-4 p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                  از تاریخ
                </label>
                <PersianDatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder="از تاریخ"
                  style={{ fontFamily: 'CustomFont', width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                  تا تاریخ
                </label>
                <PersianDatePicker
                  value={endDate}
                  onChange={setEndDate}
                  placeholder="تا تاریخ"
                  style={{ fontFamily: 'CustomFont', width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                  کارمند
                </label>
                <Select
                  value={selectedEmployee}
                  onChange={(value) => setSelectedEmployee(value ?? 'all')}
                  style={{ fontFamily: 'CustomFont', width: '100%' }}
                  placeholder="همه کارمندان"
                  allowClear
                  showSearch
                  filterOption={(input, option) => 
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  <Option value="all">همه</Option>
                  {employees.map((emp) => (
                    <Option key={emp.id} value={String(emp.id)}>
                      {emp.name || '--'}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                  نوع مرخصی
                </label>
                <Select
                  value={selectedLeaveType}
                  onChange={(value) => setSelectedLeaveType(value ?? 'all')}
                  style={{ fontFamily: 'CustomFont', width: '100%' }}
                  placeholder="همه انواع"
                  allowClear
                  showSearch
                  filterOption={(input, option) => 
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  <Option value="all">همه</Option>
                  {leaveTypes.map((type) => (
                    <Option key={type.id} value={String(type.id)}>
                      {type.type_name || type.name || '--'}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                  وضعیت
                </label>
                <Select
                  value={selectedStatus}
                  onChange={(value) => setSelectedStatus(value ?? 'all')}
                  style={{ fontFamily: 'CustomFont', width: '100%' }}
                  placeholder="همه وضعیت‌ها"
                  allowClear
                >
                  <Option value="all">همه</Option>
                  {statusOptions.map((status) => (
                    <Option key={status.value} value={status.value}>
                      {status.label}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-gray-100 dark:border-gray-700">
              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetFilters}
                className="border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-all"
                style={{ fontFamily: 'CustomFont' }}
              >
                ریست فیلترها
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* جستجو و تعداد */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3 flex-1" style={{ maxWidth: '800px' }}>
          <Input
            placeholder="جستجو..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined className="text-gray-400" />}
            style={{ fontFamily: 'CustomFont', flex: 1 }}
          />
        </div>
        <Button
          icon={<FileExcelOutlined />}
          onClick={handleExportExcel}
          loading={exporting}
          style={{ fontFamily: 'CustomFont' }}
        >
          خروجی اکسل
        </Button>
      </div>

      {/* عملیات گروهی */}
      {selectedRowKeys.length > 0 && bulkActionMenuItems.length > 0 && (
        <div className={BULK_ACTION_BAR_CLASS}>
          <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
            {selectedRowKeys.length} مورد انتخاب شده
          </span>
          <Dropdown
            menu={{ items: bulkActionMenuItems }}
            trigger={['click']}
          >
            <Button
              type="primary"
              loading={bulkActionLoading}
              size="middle"
              className="border-0"
              icon={<DownOutlined />}
              style={{ fontFamily: 'CustomFont', backgroundColor: '#f97316', color: '#fff' }}
            >
              عملیات گروهی
            </Button>
          </Dropdown>
        </div>
      )}

      {/* جدول */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {loading ? (
          <div className="p-6">
            <TableSkeleton rows={5} columns={6} />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={mappedData}
            rowSelection={rowSelection}
            loading={false}
            pagination={{
              current: pagination.current_page || 1,
              total: pagination.total || 0,
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => (
                <span style={{ fontFamily: 'CustomFont' }}>
                  نمایش {range[0]} تا {range[1]} از {total} ردیف
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            locale={{
              emptyText: (
                <Empty
                  description={<span style={{ fontFamily: 'CustomFont' }}>هیچ داده ای در جدول وجود ندارد</span>}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            scroll={{ x: 1000 }}
            rowKey="id"
            style={{ fontFamily: 'CustomFont' }}
          />
        )}
      </div>
    </div>
  );
};

export default LeavesListView;
